import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, ArrowUpDown, Check, Delete, Gem, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import AnimatedButton from './AnimatedButton'
import { hapticImpact, hapticSelectionChanged } from '../utils/haptic'

const QUICK_AMOUNTS = [100, 500, 1000, 5000, 10000, 100000]
const BALANCE_PERCENTAGES = [25, 50, 75]

const MobileBetSheet = ({
  isOpen = true,
  variant = 'overlay',
  mode,
  target,
  targetLabel,
  targets = [],
  balance = 0,
  inventory = [],
  minBet = 100,
  maxBet = 2500000,
  maxItems = Infinity,
  isDisabled = false,
  disabledReason,
  isLoading = false,
  isDataReady = true,
  onClose,
  onSubmit,
  onTopUp,
  extraContent,
  externalError,
  resetKey
}) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const closeRef = useRef(null)
  const dialogRef = useRef(null)
  const scrollRef = useRef(null)
  const tabId = useId()
  const onCloseRef = useRef(onClose)
  const submittingRef = useRef(false)
  onCloseRef.current = onClose
  const [crystalValue, setCrystalValue] = useState('')
  const [selectedIds, setSelectedIds] = useState([])
  const [selectedTarget, setSelectedTarget] = useState(null)
  const [activeTab, setActiveTab] = useState('crystals')
  const [sortOrder, setSortOrder] = useState('desc')
  const [errorText, setErrorText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isInline = variant === 'inline'
  const needsTarget = mode === 'double' || mode === 'wheel500'
  const activeTarget = needsTarget ? (isInline ? selectedTarget : target) : true
  const key = mode === 'royale' ? 'royale.betModal' : mode === 'double' ? 'double.bet' : mode === 'crash' ? 'crash.bet' : 'wheel500.bet'
  const royaleKeys = { crystals: 'crystalsTitle', enterCrystals: 'inputPlaceholder', max: 'maxLabel', clearCrystals: 'clearCrystals', topUp: 'topUpBalance', items: 'itemsTitle', sending: 'submitting' }
  const betText = (field, fallback) => t(`${key}.${mode === 'royale' ? royaleKeys[field] || field : field}`, fallback)
  const min = Math.max(0, Number(minBet) || 0)
  const max = Math.max(min, Number(maxBet) || 0)
  const availableBalance = Math.max(0, Number(balance) || 0)
  const groupSeparator = new Intl.NumberFormat(i18n.language).formatToParts(1000).find((part) => part.type === 'group')?.value
  const formattedCrystalValue = crystalValue === '' ? '' : Number(crystalValue).toLocaleString(i18n.language)
  const crystal = Math.min(Number(crystalValue) || 0, availableBalance)

  const availableInventory = useMemo(() => inventory.filter((item) =>
    Number(item.price ?? item.cost ?? 0) <= max
  ), [inventory, max])
  const selectedItems = useMemo(() => availableInventory.filter((item) =>
    selectedIds.includes(item.id)
  ), [availableInventory, selectedIds])
  const itemsTotal = selectedItems.reduce((sum, item) => sum + Number(item.price ?? item.cost ?? 0), 0)
  const maxCrystal = Math.min(availableBalance, Math.max(0, max - itemsTotal))
  const quickAmounts = QUICK_AMOUNTS.filter((amount) => amount <= maxCrystal - crystal)
  const total = crystal + itemsTotal
  const hasCrystals = availableBalance > 0
  const hasItems = availableInventory.length > 0
  const hasFunds = hasCrystals || hasItems
  const hasBoth = hasCrystals && hasItems
  const visibleTab = hasBoth ? activeTab : hasCrystals ? 'crystals' : 'items'
  const missing = Math.max(0, min - total)
  const excess = Math.max(0, total - max)
  const itemLimitExceeded = selectedIds.length > maxItems
  const canSubmit = Boolean(activeTarget) && Boolean(onSubmit) && !isDisabled && !isLoading && !isSubmitting && !itemLimitExceeded && total >= min && total <= max && total > 0
  const showLimitAlert = !isDisabled && !isLoading && !isSubmitting && total > 0 && (missing > 0 || excess > 0)
  const buttonLabel = isLoading || isSubmitting
    ? betText('sending', 'Отправка...')
    : isDisabled
      ? disabledReason || (mode === 'royale' ? t('royale.buttons.betsClosed') : t(`${key}.locked`, 'Ставки не принимаются'))
      : itemLimitExceeded
        ? t('realtime.itemLimit', 'Достигнут лимит предметов в ставке')
        : excess > 0
        ? t('wheel500.toasts.maxBet', 'Превышен лимит ставки')
        : total > 0 && missing > 0
          ? t(`${key}.missingAmount`, mode === 'royale' ? 'Не хватает: {{amount}}' : 'Не хватает {{amount}}', { amount: missing.toLocaleString(i18n.language) })
          : isInline && !activeTarget
            ? t(mode === 'double' ? 'double.desktopBet.selectColor' : 'wheel500.desktopBet.selectZone')
            : betText('submit', 'Сделать ставку')
  const sortedInventory = useMemo(() => [...availableInventory].sort((a, b) => {
    const difference = Number(a.price ?? a.cost ?? 0) - Number(b.price ?? b.cost ?? 0)
    return sortOrder === 'asc' ? difference : -difference
  }), [availableInventory, sortOrder])

  useEffect(() => {
    setCrystalValue((current) => current === '' ? '' : String(Math.min(Number(current), availableBalance)))
  }, [availableBalance])

  useEffect(() => {
    if (isOpen && !isInline) setActiveTab('crystals')
  }, [isOpen, isInline])

  useEffect(() => {
    if (resetKey != null) setSelectedIds([])
  }, [resetKey])

  useEffect(() => {
    const ids = new Set(inventory.map((item) => item.id))
    setSelectedIds((current) => current.filter((id) => ids.has(id)))
  }, [inventory])

  useEffect(() => {
    if (!isOpen || isInline) return undefined
    const previousFocus = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onCloseRef.current?.()
      if (event.key !== 'Tab') return
      const focusable = dialogRef.current?.querySelectorAll('button:not([disabled]), input:not([disabled])')
      if (!focusable?.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && (document.activeElement === first || !dialogRef.current.contains(document.activeElement))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current.contains(document.activeElement))) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
      previousFocus?.focus?.()
    }
  }, [isOpen, isInline])

  const selectTab = (tab) => {
    if (tab === visibleTab) return
    hapticSelectionChanged()
    setActiveTab(tab)
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }

  const setAmount = (value) => {
    hapticImpact('light')
    setCrystalValue(String(Math.max(0, Math.min(Math.floor(value), maxCrystal))))
    setErrorText('')
  }

  const submit = async () => {
    if (!canSubmit || submittingRef.current) return
    submittingRef.current = true
    setErrorText('')
    setIsSubmitting(true)
    try {
      const success = await onSubmit({ ...(needsTarget ? { [mode === 'double' ? 'color' : 'zone']: activeTarget } : {}), crystal, items: selectedItems.map((item) => item.id) })
      if (success) {
        setCrystalValue('')
        setSelectedIds([])
        setSelectedTarget(null)
        setSortOrder('desc')
      }
    } catch {
      setErrorText(t(mode === 'crash' ? 'crash.alerts.server_error' : mode === 'royale' ? 'royale.toasts.betFailed' : mode === 'double' ? 'double.toasts.betError' : 'wheel500.toasts.betError'))
    } finally {
      submittingRef.current = false
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  const form = (
    <>
      <div ref={scrollRef} className={`${isInline ? 'space-y-4 p-4' : 'space-y-5 px-4 py-5 overscroll-contain'} min-h-0 flex-1 overflow-y-auto`}>
        {isDataReady && isInline && needsTarget && hasFunds && (
          <div className={`grid gap-2 ${targets.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
            {targets.map((option) => (
              <button key={option.value} type="button" aria-label={option.ariaLabel || option.label} aria-pressed={activeTarget === option.value} disabled={isLoading || isSubmitting} onClick={() => {
                hapticSelectionChanged()
                setSelectedTarget(option.value)
                setErrorText('')
              }} className={`min-w-0 rounded-lg border px-1 py-2 text-xs font-semibold transition-all active:scale-[0.98] disabled:opacity-40 ${activeTarget === option.value ? 'ring-2 ring-white/50' : 'opacity-85 hover:opacity-100'} ${option.className || ''}`} style={option.style}>
                {option.label}
              </button>
            ))}
          </div>
        )}

        {isDataReady && hasBoth && (
          <div role="tablist" className="flex rounded-xl border border-white/10 bg-surface p-1" onKeyDown={(event) => {
            const tabs = ['crystals', 'items']
            const direction = i18n.dir() === 'rtl' ? -1 : 1
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? 1
              : event.key === 'ArrowRight' ? (tabs.indexOf(visibleTab) + direction + 2) % 2
                : event.key === 'ArrowLeft' ? (tabs.indexOf(visibleTab) - direction + 2) % 2 : null
            if (next === null) return
            event.preventDefault()
            selectTab(tabs[next])
            event.currentTarget.querySelectorAll('[role="tab"]')[next]?.focus()
          }}>
            {['crystals', 'items'].map((tab) => (
              <button key={tab} id={`${tabId}-${tab}-tab`} type="button" role="tab" aria-selected={visibleTab === tab} aria-controls={`${tabId}-panel`} tabIndex={visibleTab === tab ? 0 : -1} onClick={() => selectTab(tab)} className={`flex min-w-0 flex-1 items-center justify-center gap-2 rounded-lg px-2 py-2.5 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-primary ${visibleTab === tab ? 'bg-white/10 text-white' : 'text-text-secondary hover:text-white'}`}>
                {betText(tab === 'crystals' ? 'crystals' : 'items', tab === 'crystals' ? 'Кристаллы' : 'Предметы')}
                {(tab === 'crystals' ? crystal > 0 : selectedItems.length > 0) && <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent-primary" />}
              </button>
            ))}
          </div>
        )}

        {isDataReady && hasCrystals && visibleTab === 'crystals' && (
          <section id={hasBoth ? `${tabId}-panel` : undefined} role={hasBoth ? 'tabpanel' : undefined} aria-labelledby={hasBoth ? `${tabId}-crystals-tab` : undefined}>
            <div className={`mb-3 flex items-center ${hasBoth ? 'justify-start' : 'justify-between'}`}>
              {!hasBoth && <h3 className="font-semibold text-white">{betText('crystals', 'Кристаллы')}</h3>}
              <span className="inline-flex items-center gap-1 text-sm font-bold tabular-nums text-accent-primary">
                <Gem className="h-4 w-4" />{availableBalance.toLocaleString(i18n.language)}
              </span>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-surface px-3 focus-within:border-accent-primary/50">
              <Gem className="h-5 w-5 shrink-0 text-accent-primary" />
              <input type="text" inputMode="numeric" value={formattedCrystalValue} onKeyDown={(event) => {
                const field = event.currentTarget
                if (field.selectionStart !== field.selectionEnd) return
                if (event.key === 'Backspace' && field.value[field.selectionStart - 1] === groupSeparator) {
                  field.setSelectionRange(field.selectionStart - 1, field.selectionStart - 1)
                } else if (event.key === 'Delete' && field.value[field.selectionStart] === groupSeparator) {
                  field.setSelectionRange(field.selectionStart + 1, field.selectionStart + 1)
                }
              }} onChange={(event) => {
                const field = event.currentTarget
                const input = field.value
                if ([...input].some((character) => !/\d/.test(character) && character !== groupSeparator && !/\s/.test(character))) return
                const digits = input.replace(/\D/g, '')
                const nextValue = digits === '' ? '' : String(Math.min(Number(digits), availableBalance))
                const digitsBeforeCaret = Math.min(input.slice(0, field.selectionStart).replace(/\D/g, '').length, nextValue.length)
                const atEnd = field.selectionStart === input.length
                field.value = nextValue === '' ? '' : Number(nextValue).toLocaleString(i18n.language)
                let position = 0
                let remaining = digitsBeforeCaret
                while (position < field.value.length && remaining > 0) {
                  if (/\d/.test(field.value[position])) remaining -= 1
                  position += 1
                }
                field.setSelectionRange(atEnd ? field.value.length : position, atEnd ? field.value.length : position)
                setCrystalValue(nextValue)
                setErrorText('')
              }} placeholder={betText('enterCrystals', 'Введите сумму')} aria-label={betText('enterCrystals', 'Введите сумму')} className="min-w-0 w-full bg-transparent py-3 text-lg font-semibold tabular-nums text-white outline-none" />
              {crystalValue !== '' && <button type="button" onClick={() => { setCrystalValue(''); setErrorText('') }} aria-label={betText('clearCrystals', 'Очистить сумму')} className="p-2 text-text-secondary active:scale-[0.98]"><Delete className="h-4 w-4" /></button>}
            </div>
            {quickAmounts.length > 0 && <div className="mt-3 grid grid-cols-3 gap-2">
              {quickAmounts.map((amount) => <button key={amount} type="button" onClick={() => setAmount(crystal + amount)} className="rounded-lg border border-white/10 bg-surface px-2 py-2 text-xs font-semibold text-text-secondary active:scale-[0.98]">+{amount.toLocaleString(i18n.language)}</button>)}
            </div>}
            <div className={`${quickAmounts.length > 0 ? 'mt-2' : 'mt-3'} grid grid-cols-4 gap-2`}>
              {BALANCE_PERCENTAGES.map((percentage) => <button key={percentage} type="button" onClick={() => setAmount(availableBalance * percentage / 100)} disabled={maxCrystal <= 0} aria-label={`${percentage}% · ${t('home.balance', 'Баланс')}`} className="rounded-lg border border-white/10 bg-surface px-2 py-2 text-xs font-semibold text-text-secondary active:scale-[0.98] disabled:opacity-40">{percentage}%</button>)}
              <button type="button" onClick={() => setAmount(maxCrystal)} disabled={maxCrystal <= 0} className="rounded-lg border border-white/10 bg-surface px-2 py-2 text-xs font-semibold text-text-secondary active:scale-[0.98] disabled:opacity-40">{betText('max', 'MAX')}</button>
            </div>
          </section>
        )}

        {isDataReady && hasItems && visibleTab === 'items' && (
          <section id={hasBoth ? `${tabId}-panel` : undefined} role={hasBoth ? 'tabpanel' : undefined} aria-labelledby={hasBoth ? `${tabId}-items-tab` : undefined}>
            <div className={`mb-3 flex items-center ${hasBoth ? 'justify-start' : 'justify-between'}`}>
              {!hasBoth && <h3 className="font-semibold text-white">{betText('items', 'Предметы')}</h3>}
              <button type="button" onClick={() => setSortOrder((order) => order === 'asc' ? 'desc' : 'asc')} aria-label={t('common.sortByPrice')} aria-pressed={sortOrder === 'asc'} className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-surface text-text-secondary active:scale-[0.98]">
                <ArrowUpDown className={`h-4 w-4 transition-transform ${sortOrder === 'asc' ? 'rotate-180 text-accent-primary' : ''}`} />
              </button>
            </div>
            <div className={`grid grid-cols-3 gap-2 ${isInline ? '' : 'lg:grid-cols-4'}`}>
              {sortedInventory.map((item) => {
                const selected = selectedIds.includes(item.id)
                const limitReached = !selected && selectedIds.length >= maxItems
                return (
                  <button key={item.id} type="button" aria-pressed={selected} disabled={limitReached} title={limitReached ? t('realtime.itemLimit', 'Достигнут лимит предметов в ставке') : undefined} onClick={() => {
                    hapticSelectionChanged()
                    setSelectedIds((ids) => selected ? ids.filter((id) => id !== item.id) : [...ids, item.id])
                    setErrorText('')
                  }} className={`relative min-w-0 overflow-hidden rounded-xl border bg-surface p-2 text-left active:scale-[0.98] disabled:opacity-40 ${selected ? 'border-accent-primary' : 'border-white/10'}`}>
                    {selected && <span className="absolute right-2 top-2 z-10 rounded-full bg-accent-primary p-0.5 text-dark-primary"><Check className="h-3.5 w-3.5" /></span>}
                    {item.img ? <img src={item.img} alt="" loading="lazy" className="mx-auto aspect-square w-full object-contain" /> : <div className="flex aspect-square items-center justify-center"><Gem className="h-8 w-8 text-text-secondary" /></div>}
                    <span className="mt-1 block truncate text-xs font-medium text-white">{item.name}</span>
                    <span className="mt-1 inline-flex items-center gap-0.5 text-xs font-bold tabular-nums text-accent-primary"><Gem className="h-3 w-3" />{Number(item.price ?? item.cost ?? 0).toLocaleString(i18n.language)}</span>
                  </button>
                )
              })}
            </div>
          </section>
        )}

        {isDataReady && hasFunds && extraContent}
        {!isDataReady && <div role="status" className="py-6 text-center text-sm text-text-secondary">{t('common.loading', 'Загрузка...')}</div>}
        {isDataReady && !hasFunds && (
          <div className="flex flex-col items-center gap-4 py-6">
            <Gem className="h-10 w-10 text-accent-primary" />
            <AnimatedButton onClick={() => onTopUp ? onTopUp() : navigate('/payment')} soundFeedback={false} className="!py-3 !text-base">
              {betText('topUp', 'Пополнить баланс')}
            </AnimatedButton>
          </div>
        )}
      </div>

      {isDataReady && hasFunds && (
        <div className={`shrink-0 border-t border-white/10 ${isInline ? 'px-4 pb-4 pt-3' : 'bg-dark-primary px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3'}`}>
          <AnimatedButton onClick={submit} disabled={!canSubmit} soundFeedback={false} className={`!py-3 !text-base ${showLimitAlert ? '!border-error/40 !text-error' : ''}`}>
            <span aria-live="polite" className="inline-flex flex-wrap items-center justify-center gap-2">
              {showLimitAlert && <AlertCircle className="h-4 w-4" />}
              {buttonLabel}
              {total > 0 && !showLimitAlert && !isDisabled && activeTarget && <span className="inline-flex items-center gap-1 tabular-nums"><Gem className="h-4 w-4" />{total.toLocaleString(i18n.language)}</span>}
            </span>
          </AnimatedButton>
          {(errorText || externalError) && <p role="alert" className="mt-2 text-xs text-error">{errorText || externalError}</p>}
        </div>
      )}
    </>
  )

  if (isInline) {
    return (
      <div className="flex max-h-[calc(100dvh-6rem)] flex-col overflow-hidden rounded-2xl border border-white/10 bg-surface/50">
        <h2 className="border-b border-white/10 px-4 py-3 font-semibold text-white">{t(mode === 'royale' ? 'royale.desktopBet.title' : mode === 'double' ? 'double.desktopBet.title' : mode === 'crash' ? 'crash.title' : 'wheel500.desktopBet.title')}</h2>
        {form}
      </div>
    )
  }

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} aria-hidden="true" />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="mobile-bet-title" className="relative flex max-h-[90dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl border border-white/10 bg-dark-primary shadow-2xl sm:rounded-3xl lg:max-w-2xl">
        <div className="shrink-0 border-b border-white/10 px-4 pb-3 pt-3">
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/20 sm:hidden" />
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="hidden text-xs font-semibold uppercase tracking-wider text-text-secondary sm:block">{mode === 'royale' ? t('royale.title') : mode === 'double' ? 'Double' : mode === 'crash' ? t('crash.title') : 'X50'}</p>
              <h2 id="mobile-bet-title" className="truncate text-xl font-bold text-white">
                {mode === 'royale'
                  ? t('royale.betModal.title')
                  : mode === 'crash'
                    ? t('crash.bet.submit')
                  : mode === 'double'
                    ? `${t('double.bet.title', 'Ставка на')} ${targetLabel || target}`
                    : t('wheel500.bet.title', 'Ставка на {{zone}}', { zone: targetLabel || target })}
              </h2>
            </div>
            <button ref={closeRef} type="button" onClick={onClose} aria-label={t('common.close', 'Закрыть')} className="rounded-xl border border-white/10 bg-surface p-2.5 text-text-secondary active:scale-[0.98]">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        {form}
      </div>
    </div>,
    document.body
  )
}

export default MobileBetSheet
